import { CdkDragDrop, CdkDragStart, DragRef } from "@angular/cdk/drag-drop";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output,
	SimpleChanges,
	TemplateRef,
} from "@angular/core";
import * as _ from "lodash";
import { FilterElementsPipe } from "../../pipes/filters-elements.pipe";
import { OrderbyPipe } from "../../pipes/orderby.pipe";
import { GlobalService } from "../../services/global.service";

@Component({
    selector: "app-multi-drag-drop",
    templateUrl: "./multi-drag-drop.component.html",
    styleUrls: ["./multi-drag-drop.component.css"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MultiDragDropComponent implements OnInit {
	@Input() items: any[];
	@Input() filteredElements: any[] = null;
	@Input() emptyMsg: string = "This list is empty";
	@Input() id: string = "id";
	@Input() dropListName?: string;
	@Input() connectedList?: any[];
	@Input() orderBy?: any[] = null;
	@Input() blockDropSameList: boolean = false;
	@Input() disableDrag: boolean = false;
	@Input() disableSort: boolean = false;
	@Input() useTrackBy: boolean = true;
	@Input() trackById: string = 'id';
	@Input() minimize: boolean = false;
	@Input() minHeight: number = 85;
	@Input() maxHeight: number = null;
	@Input() autoScrollStep: number = 5;
	@Input() itemsPerPage: number = 20;
	@Input() removeItemsFromList: boolean = true;
	@Input() viewCalendarStripboard: boolean = false;
	@Input() canDrop = function () {
		return true;
	};
	@Input() infiniteScroll: boolean = false;
	@Input() infiniteScrollContainer: string;
	@Input() pointerMustBeInContainer: boolean = true;

	@Output() itemsRemoved = new EventEmitter<any[]>();
	@Output() itemsAdded = new EventEmitter<any[]>();
	@Output() itemsUpdated = new EventEmitter<any>();
	@Output() selectionChanged = new EventEmitter<any[]>();
	@Output() doubleClick = new EventEmitter<any>();
	@Output() contextClick = new EventEmitter<any>();
	@Output() changeOrder = new EventEmitter<any>();

	@ContentChild(TemplateRef, { static: false }) templateRef;

	public dragging: DragRef = null;
	public selections: number[] = [];
	public tempSelection: number;
	public isDropListEnter: boolean = false;
	private currentSelectionSpan: number[] = [];
	private lastSingleSelection: number;
	throttle = 10;
	scrollDistance = 1;
	scrollUpDistance = 2;
	itemsToShow = 20;
	refreshPipe: number = 1;
	trackByFn: Function;

	constructor(
		private eRef: ElementRef,
		private cdRef: ChangeDetectorRef,
		private orderPipe: OrderbyPipe,
		private filterPipe: FilterElementsPipe,
		public _gS: GlobalService) { }

	ngOnInit(): void {
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!this.infiniteScroll) {
			this.itemsToShow = this.items.length;
		}

		if (changes.items || changes.filteredElements || changes.orderBy) {
			let shownItems = this.getShownItems(false);
			if (this.itemsToShow > shownItems.length) {
				this.itemsToShow = shownItems.length < this.itemsPerPage ?
					this.itemsPerPage :
					(shownItems.length > this.itemsToShow ? this.itemsToShow : shownItems.length);
			}
		}
	}

	/**
	 * Used if there is filters used in pipe and return valid index.
	 * @param index
	 * @param item
	 */
	getRealIndex(index, item) {
		if (this.filteredElements || this.orderBy) {
			return this.items.findIndex((i) => i[this.id] == item[this.id]);
		}
		return index;
	}

	getShownItems(order = true) {
		if (!order) {
			return this.filterPipe.transform([...this.items], this.filteredElements);
		}
		let shownItems = this.orderPipe.transform([...this.items], this.orderBy);
		return this.filterPipe.transform([...shownItems], this.filteredElements);
	}

	dragStarted(ev: CdkDragStart, index: number, item: any): void {
		index = this.getRealIndex(index, item);
		this.dragging = ev.source._dragRef;
		const indices = this.selections.length ? this.selections : [index];
		this.tempSelection = null;
		ev.source.data = {
			indices,
			values: indices.map((i) => this.items[i]),
			source: this,
		};
		this.cdRef.detectChanges();
	}

	dragEnded(): void {
		this.dragging = null;
		this.cdRef.detectChanges();
	}

	toggleDropListEnter() {
		this.isDropListEnter = !this.isDropListEnter;
	}

	onItemsUpdated(ev, type) {
		setTimeout(() => {
			this.itemsUpdated.emit({ 'ev': ev, 'type': type });
		})
	}

	dropped(ev: CdkDragDrop<any>): void {
		this.isDropListEnter = false;
		if ((this.pointerMustBeInContainer && !ev.isPointerOverContainer) || !_.get(ev, "item.data.source")) {
			return;
		}
		const data = ev.item.data;

		if (data.source === this) {
			let removed;
			if (this.removeItemsFromList || ev.previousContainer == ev.container) {
				removed = _.pullAt(this.items, data.indices);
				setTimeout(() => this.changeOrder.emit({ 'ev': ev, 'items': this.items }));
			}
			if (ev.previousContainer !== ev.container) {
				this.itemsRemoved.emit(removed);
				this.onItemsUpdated(ev, 'removed');
			}
		}
		this.dragging = null;
		setTimeout(() => this.clearSelection());
	}

	droppedIntoList(ev: CdkDragDrop<any>): void {
		this.isDropListEnter = false;
		if ((this.pointerMustBeInContainer && !ev.isPointerOverContainer) || !_.get(ev, "item.data.source")) {
			return;
		}
		const data = ev.item.data;
		let spliceIntoIndex = ev.currentIndex;
		if (ev.previousContainer === ev.container && this.selections.length > 1) {
			this.selections.splice(-1, 1);
			const sum = _.sumBy(this.selections, (selectedIndex) =>
				selectedIndex <= spliceIntoIndex ? 1 : 0
			);
			spliceIntoIndex -= sum;
		}
		this.items.splice(spliceIntoIndex, 0, ...data.values);

		if (ev.previousContainer !== ev.container) {
			this.itemsAdded.emit(data.values);
		}
		this.onItemsUpdated(ev, 'added');
		setTimeout(() => this.cdRef.detectChanges());
	}

	isSelected(i: number, item): boolean {
		if (item) {
			i = this.getRealIndex(i, item);
			return this.selections.indexOf(i) >= 0 || this.tempSelection == i;
		}
		return false;
	}

	onDoubleClick(item, index) {
		index = this.getRealIndex(index, item);
		this.doubleClick.emit({ item: item, index: index });
	}

	onMouseDown(index, item) {
		index = this.getRealIndex(index, item);
		this.tempSelection = index;
	}

	select(event, index, item) {
		index = this.getRealIndex(index, item);
		const shiftSelect =
			event.shiftKey &&
			(this.lastSingleSelection || this.lastSingleSelection === 0) &&
			this.lastSingleSelection !== index;

		this.tempSelection = null;

		if (!this.selections || this.selections.length < 1) {
			// if nothing selected yet, init selection mode and select.
			this.selections = [index];
			this.lastSingleSelection = index;
		} else if (event.metaKey || event.ctrlKey) {
			// if holding ctrl / cmd
			const alreadySelected = _.find(this.selections, (s) => s === index);
			if (alreadySelected) {
				_.remove(this.selections, (s) => s === index);
				this.lastSingleSelection = null;
			} else {
				this.selections.push(index);
				this.lastSingleSelection = index;
			}
		} else if (shiftSelect) {
			// if holding shift, add group to selection and currentSelectionSpan
			const newSelectionBefore = index < this.lastSingleSelection;
			const count = newSelectionBefore
				? this.lastSingleSelection - (index - 1)
				: index + 1 - this.lastSingleSelection;

			// clear previous shift selection
			if (this.currentSelectionSpan && this.currentSelectionSpan.length > 0) {
				_.each(this.currentSelectionSpan, (i) => {
					_.remove(this.selections, (s) => s === i);
				});
				this.currentSelectionSpan = [];
			}
			// build new currentSelectionSpan
			_.times(count, (c) => {
				if (newSelectionBefore) {
					this.currentSelectionSpan.push(this.lastSingleSelection - c);
				} else {
					this.currentSelectionSpan.push(this.lastSingleSelection + c);
				}
			});
			// select currentSelectionSpan
			_.each(this.currentSelectionSpan, (i) => {
				if (!_.includes(this.selections, i)) {
					this.selections.push(i);
				}
			});

			// Needs real index when using sort or order
			if (this.filteredElements || this.orderBy) {
				let shownItems = this.getShownItems();

				let indexFirst = this.selections.shift();
				let indexLast = this.selections.pop();

				let itemFirst = this.items[indexFirst];
				let itemLast = this.items[indexLast];

				let indexInShownFirst = shownItems.findIndex(i => i.id == itemFirst.id);
				let indexInShownLast = shownItems.findIndex(i => i.id == itemLast.id);

				let first = indexInShownFirst < indexInShownLast ? indexInShownFirst : indexInShownLast;
				let last = indexInShownFirst < indexInShownLast ? indexInShownLast : indexInShownFirst;

				let elementsInShown = shownItems.slice(first, last + 1);
				this.selections = elementsInShown.map(e => {
					return this.items.findIndex(i => i.id == e.id);
				})

			}

		} else {
			// Select only this item or clear selections.
			const alreadySelected = _.find(this.selections, (s) => s === index);
			if (
				(!alreadySelected && !event.shiftKey) ||
				(alreadySelected && this.selections.length > 1)
			) {
				this.clearSelection();
				this.selections = [index];
				this.lastSingleSelection = index;
			} else if (alreadySelected) {
				this.clearSelection();
			}
		}

		if (!event.shiftKey) {
			this.currentSelectionSpan = [];
		}
		this.selectionChanged.emit(this.selections.map((i) => this.items[i]));
		this.cdRef.detectChanges();
	}

	clearSelection() {
		if (this.selections.length) {
			this.selections = [];
			this.currentSelectionSpan = [];
			this.lastSingleSelection = null;
			this.selectionChanged.emit(this.selections.map((i) => this.items[i]));
			this.cdRef.detectChanges();
		}
	}

	selectAll() {
		if (this.filteredElements || this.orderBy) {
			let shownItems = this.getShownItems();
			this.selections = shownItems.map(e => {
				return this.items.findIndex(i => i.id == e.id);
			});
		} else {
			if (this.selections.length !== this.items.length) {
				this.selections = _.map(this.items, (item, i) => i);
			}
		}
		this.currentSelectionSpan = [];
		this.lastSingleSelection = null;
		this.selectionChanged.emit(this.items);
		this.cdRef.detectChanges();
	}

	onScroll(ev) {
		this.itemsToShow += this.itemsPerPage;
	}

	onSelectedItem(ev, item) {
		this._gS.rigthClick(ev, 3);
		this.contextClick.emit(item);
	}

	disableSortOrientation(item) {
		if (item?.type_array == 2) return 'y';
		else return '';
	}

	// handles "ctrl/command + a" to select all
	@HostListener("document:keydown", ["$event"])
	protected handleKeyboardEvent(event: KeyboardEvent) {
		if (
			event.key === "a" &&
			(event.ctrlKey || event.metaKey) &&
			this.selections.length &&
			document.activeElement.nodeName !== "INPUT"
		) {
			event.preventDefault();
			this.selectAll();
		} else if (event.key === "Escape" && this.dragging) {
			this.dragging.reset();
			document.dispatchEvent(new Event("mouseup"));
		}
	}

	@HostListener("document:click", ["$event"])
	protected clickout(event) {
		if (
			this.selections.length &&
			!this.eRef.nativeElement.contains(event.target)
		) {
			this.clearSelection();
		}
	}
}
